import { useMemo } from "react";
import { Button } from "../../../../packages/ui-kit";
import { PointLoader } from "../../../../packages/components";
import { ArrowDownIcon } from "../../../../packages/icons";
import { useNetworkContext } from "../../../network";
import { useInputHeightContext, useMessagesContext } from "../../contexts";
import {
  ServiceMessageDate,
  ServiceMessageNew,
  useResendMessage,
} from "../message-kinds";
import { ChatEmpty } from "../chat-empty";
import { ChatMessage } from "../chat-message";
import { useMessageList } from "./use-message-list";
import { useMessageReading } from "./use-message-reading";
import styles from "./chat-message-list.module.scss";

export const ChatMessageList = () => {
  const { messages } = useMessagesContext();
  const { handleReadMessage } = useMessageReading();
  const { handleResendMessage } = useResendMessage();
  const { downButtonOffset } = useInputHeightContext();

  const {
    isGetMessagesPending,
    isGetMessagesLoading,
    isPrevMessagesLoading,
    isNextMessagesLoading,
  } = useNetworkContext();

  const {
    scrollRef,
    unreadCount,
    firstUnreadMessageId,
    isScrolling,
    handleScroll,
    handleScrollDownClick,
    shouldShownScrollDownButton,
  } = useMessageList();

  const shouldShownCenterLoader =
    (isGetMessagesPending && !messages.length) || isGetMessagesLoading;

  const messagesList = useMemo(
    () =>
      messages.map(({ items, date }) => (
        <div data-test-id="chat-message-date" className={styles.day} key={date}>
          <ServiceMessageDate date={date} isScrolling={isScrolling} />
          {items.map((message) => {
            const { id, traceId } = message;
            const handleRead = handleReadMessage(id);
            const handleResend = handleResendMessage(traceId);
            const shouldShownUnreadNotification = firstUnreadMessageId === id;

            return (
              // TODO проверить необходимость этих стилей в dev-среде. Возможно стоит переписать иначе
              <div
                id={message.id}
                className={styles["message-wrapper"]}
                key={`${traceId}-wrapper`}
              >
                {shouldShownUnreadNotification && <ServiceMessageNew />}
                <ChatMessage
                  key={traceId}
                  message={message}
                  onReadMessage={handleRead}
                  onResendMessage={handleResend}
                />
              </div>
            );
          })}
        </div>
      )),
    [messages, /* lastReadMessageId, */ isScrolling]
  );

  if (!messages.length) {
    return <ChatEmpty />;
  }

  const loader = (
    <div
      data-test-id="chat-message-list-prev-loader"
      className={styles["prev-loader"]}
    >
      <PointLoader />
    </div>
  );

  const downButton = (
    <div
      className={styles["scroll-down-button-container"]}
      style={{ bottom: `${downButtonOffset}px` }}
    >
      {!!unreadCount && <span className={styles["new-message"]} />}
      <Button
        dataTestId="chat-message-list-scroll-down-button"
        className={styles["scroll-down-button"]}
        onClick={handleScrollDownClick}
        type="circle"
      >
        <ArrowDownIcon size="large" />
      </Button>
    </div>
  );

  return (
    <>
      {shouldShownScrollDownButton && downButton}
      <div
        data-test-id="chat-message-list"
        className={styles["message-list"]}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <div data-test-id="chat-message-list-dummy" className={styles.dummy} />
        {(shouldShownCenterLoader || isPrevMessagesLoading) && loader}
        {messagesList}
        {isNextMessagesLoading && loader}
      </div>
    </>
  );
};
